import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Collapse, Input, Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import evmAdminService from '../../../shared/services/evmAdminService';

const { Text } = Typography;
const errMsg = (res, fallback) => res?.msg || res?.error || fallback;

// 节点故障转移：查看计划 → 输入节点名确认 → 标记故障并在其它节点重启云服务器
const NodeHaModal = ({ node, nodes = [], onClose, onChanged }) => {
    const [plan, setPlan] = useState(null);
    const [loading, setLoading] = useState(false);
    const [confirm, setConfirm] = useState('');
    const [target, setTarget] = useState(undefined);
    const [noFence, setNoFence] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const nodeId = node?.id;

    const load = useCallback(async () => {
        if (!nodeId) return;
        setLoading(true);
        const params = { ...(target ? { target_node_id: target } : {}), ...(noFence ? { no_fence: 1 } : {}) };
        const res = await evmAdminService.getNodeHa(nodeId, params);
        setLoading(false);
        if (res?.status === 'success') setPlan(res.data || null);
        else message.error(errMsg(res, '加载失败'));
    }, [nodeId, target, noFence]);

    useEffect(() => {
        setPlan(null); setConfirm(''); setTarget(undefined); setNoFence(false); setResult(null);
    }, [nodeId]);
    useEffect(() => { load(); }, [load]);

    const nodeName = Object.fromEntries(nodes.map((n) => [n.id, n.name]));
    const dead = plan?.node?.ha_state === 'dead';
    const name = plan?.node?.name ?? node?.name ?? '';

    const declare = async () => {
        setSubmitting(true);
        const body = { confirm };
        if (target) body.target_node_id = target;
        if (noFence) body.no_fence = true;
        const res = await evmAdminService.declareNodeDead(nodeId, body);
        setSubmitting(false);
        if (res?.status === 'success') {
            message.success(res.msg || '已提交');
            setResult(res.data?.vms || []);
            onChanged?.();
            load();
        } else message.error(errMsg(res, '操作失败'));
    };

    const clear = async () => {
        const res = await evmAdminService.clearNodeHa(nodeId);
        if (res?.status === 'success') { message.success(res.msg || '已恢复'); onChanged?.(); load(); } else message.error(errMsg(res, '操作失败'));
    };

    const rows = result || plan?.vms || [];
    const columns = [
        { title: '云服务器', dataIndex: 'name', render: (v, r) => v || `#${r.vm_id}` },
        { title: '可重启', dataIndex: 'restartable', width: 80, render: (v) => (v ? <Tag color="success">是</Tag> : <Tag>否</Tag>) },
        { title: '目标节点', dataIndex: 'target_node_id', width: 130, render: (v) => (v ? (nodeName[v] || `#${v}`) : '-') },
        {
            title: result ? '任务' : '原因', width: 160,
            render: (_, r) => (result
                ? (r.task_id ? `#${r.task_id}` : <Text type="secondary">{r.reason || '-'}</Text>)
                : <Text type="secondary">{r.reason || '-'}</Text>),
        },
    ];

    return (
        <Modal
            title={<Space>故障转移 {name}{dead && <Tag color="error">已标记故障</Tag>}</Space>}
            open={!!node}
            onCancel={onClose}
            width={680}
            destroyOnClose
            footer={dead
                ? [
                    <Button key="close" onClick={onClose}>关闭</Button>,
                    <Popconfirm key="clear" title="节点已修复，恢复调度？" onConfirm={clear}>
                        <Button type="primary">恢复节点</Button>
                    </Popconfirm>,
                ]
                : [
                    <Button key="close" onClick={onClose}>取消</Button>,
                    <Button key="ok" danger type="primary" loading={submitting} disabled={!name || confirm !== name || !!result} onClick={declare}>
                        标记故障并重启
                    </Button>,
                ]}
        >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Table rowKey="vm_id" size="small" loading={loading} columns={columns} dataSource={rows}
                    pagination={{ pageSize: 8, hideOnSinglePage: true }} />
                {!dead && !result && (
                    <>
                        <Alert type="error" showIcon message={<>节点将被停用，其上云服务器在其它节点重启。输入 <Text code>{name}</Text> 确认</>} />
                        <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={name} status={confirm && confirm !== name ? 'error' : undefined} />
                        <Collapse ghost size="small" items={[{
                            key: 'adv',
                            label: '高级',
                            children: (
                                <Space direction="vertical">
                                    <Space>
                                        <Text>目标节点</Text>
                                        <Select allowClear placeholder="自动" style={{ width: 200 }} value={target} onChange={setTarget}
                                            options={nodes.filter((n) => n.id !== nodeId).map((n) => ({ value: n.id, label: n.name }))} />
                                    </Space>
                                    <Tooltip title="不尝试隔离原节点，必须确认已断电，否则可能双写损坏磁盘；NFS/iSCSI 盘的实例只有勾选后才会重启">
                                        <Checkbox checked={noFence} onChange={(e) => setNoFence(e.target.checked)}>确认节点已断电（跳过隔离）</Checkbox>
                                    </Tooltip>
                                </Space>
                            ),
                        }]} />
                    </>
                )}
            </Space>
        </Modal>
    );
};

export default NodeHaModal;
